interface TrendLine {
  slope: number
  intercept: number
  // Coefficient of determination (R²) of the fit, in [0, 1].
  r2: number
}

/**
 * Ordinary least-squares fit. Returns null when there are fewer than two points
 * or x has no variance (the fit would be a vertical, undefined line).
 */
export function computeTrendLine(
  points: { x: number; y: number }[],
): TrendLine | null {
  if (points.length < 2) {
    return null
  }
  const n = points.length
  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumXX = 0
  let sumYY = 0
  for (const { x, y } of points) {
    sumX += x
    sumY += y
    sumXY += x * y
    sumXX += x * x
    sumYY += y * y
  }
  const xDenominator = n * sumXX - sumX * sumX
  if (xDenominator === 0) {
    return null
  }
  const numerator = n * sumXY - sumX * sumY
  const slope = numerator / xDenominator
  const intercept = (sumY - slope * sumX) / n
  // r² = squared Pearson correlation; 0 when y has no variance to explain.
  const yDenominator = n * sumYY - sumY * sumY
  const r2 =
    yDenominator === 0
      ? 0
      : (numerator * numerator) / (xDenominator * yDenominator)
  return { slope, intercept, r2 }
}
